// Generic helper for firing off several JSON GET requests concurrently using a small
// pool of Web Workers, instead of awaiting one request at a time on the main thread.
//
// This mirrors the pattern already used for parallel file downloads (see
// `src/utils/downloader/downloadMultiple.ts`'s `createWorker`), but fetches + parses
// JSON responses (and reads the `total-count` header) instead of downloading blobs.

export interface ParallelFetchTask {
  url: string;
  headers?: Record<string, string>;
}

export interface ParallelFetchResult<T = any> {
  index: number;
  data: T;
  totalCount: number;
}

export interface RunParallelJsonFetchesOptions<T = any> {
  tasks: ParallelFetchTask[];
  numWorkers?: number;
  onResult: (result: ParallelFetchResult<T>) => void;
  onError?: (index: number, error: string) => void;
}

const createJsonFetchWorker = (): Worker => {
  return new Worker(
    URL.createObjectURL(
      new Blob(
        [
          `self.onmessage = async (event) => {
            const { id, url, headers } = event.data;
            try {
              const response = await fetch(url, { headers: headers || {} });
              if (!response.ok) {
                throw new Error('HTTP ' + response.status + ' - ' + response.statusText);
              }
              const data = await response.json();
              const totalCount = parseInt(response.headers.get('total-count') || '0', 10);
              self.postMessage({ id, success: true, data, totalCount });
            } catch (error) {
              self.postMessage({ id, success: false, error: error && error.message ? error.message : String(error) });
            }
          };`,
        ],
        { type: 'application/javascript' }
      )
    )
  );
};

/**
 * Runs a list of JSON GET-fetch tasks using a small pool of Web Workers so multiple
 * requests can be in-flight at the same time (instead of sequentially awaiting each
 * one on the main thread). Results stream back via `onResult` as soon as each task
 * completes - order of completion is NOT guaranteed, use `index` to reorder.
 */
export const runParallelJsonFetches = async <T = any>({
  tasks,
  numWorkers = 4,
  onResult,
  onError,
}: RunParallelJsonFetchesOptions<T>): Promise<void> => {
  if (tasks.length === 0) return;

  const queue = tasks.map((task, index) => ({ ...task, index }));
  const workerCount = Math.min(numWorkers, queue.length);
  const workers: Worker[] = Array.from({ length: workerCount }, () =>
    createJsonFetchWorker()
  );

  await Promise.all(
    workers.map(
      (worker) =>
        new Promise<void>((resolve) => {
          const assignNext = () => {
            const next = queue.shift();
            if (!next) {
              worker.terminate();
              resolve();
              return;
            }
            worker.onmessage = (event) => {
              const { success, data, totalCount, error } = event.data;
              if (success) {
                onResult({ index: next.index, data, totalCount });
              } else {
                onError?.(next.index, error);
              }
              assignNext();
            };
            worker.onerror = (event) => {
              onError?.(next.index, event.message || 'Worker error');
              assignNext();
            };
            worker.postMessage({
              id: next.index,
              url: next.url,
              headers: next.headers,
            });
          };
          assignNext();
        })
    )
  );
};
